import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import { useTrackChangesQuery } from 'components/WorkspaceEditor/components/TrackChangesPanel/api'
import {
    useContentMutationService,
    useTransactionManager,
} from 'components/WorkspaceEditor/services'
import { ContentItemViewNodesType } from 'components/WorkspaceEditor/types'
import {
    useShallowSelector,
    useWorkspace2Store,
} from 'components/WorkspaceEditor/Workspace2Store'
import { debounce } from 'lodash'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { PROPERTY_EDITOR_PILL_STATE_VARIANT } from '../constants'
import {
    updateElementById,
    updateElements,
    usePillSelectionSelector,
} from '../store/PillSelectionStore'

const DEBOUNCE_INTERVAL = 300

/**
 * Hook that encapsulates pill selection functionality for text content elements
 *
 * @returns An object containing state and functions for pill selection operations
 */
export const usePillSelection = () => {
    const {
        nodes,
        previousNodes,
        setContentItemViewNodes,
        setTextContentElementToAdd,
    } = useShallowSelector((state) => ({
        nodes: state.contentItemViewNodes,
        previousNodes: state.previousContentItemViewNodes,
        textContentElementtoAdd: state.textContentElementToAdd,
        setContentItemViewNodes: state.actions.setContentItemViewNodes,
        setTextContentElementToAdd: state.actions.setTextContentElementToAdd,
    }))    

    const {
        isValidVariant,
        calculatePillStateChange,
        setPillSelection,
        resetPillSelectionState,
    } = usePillSelectionSelector((s) => ({
        isValidVariant: s.isValidVariant,
        calculatePillStateChange: s.calculatePillStateChange,
        setPillSelection: s.setPillSelection,
        resetPillSelectionState: s.resetPillSelectionState,
    }))

    const { executeWithOptimistic } = useTransactionManager()
    const { mutateContentElements } = useContentMutationService()
    const { fetchTrackChangesData } = useTrackChangesQuery()

    // Track component mount status
    const isMountedRef = useRef(true)

    useEffect(() => {
        return () => {
            isMountedRef.current = false
        }
    }, [])

    // Debounced fetch to prevent race conditions
    const debouncedFetchTrackChanges = useMemo(
        () =>
            debounce(async () => {
                if (!isMountedRef.current) return

                try {
                    await fetchTrackChangesData()
                } catch (error) {
                    if (!isMountedRef.current) return
                    console.error('Failed to fetch track changes data:', error)
                }
            }, DEBOUNCE_INTERVAL),
        [fetchTrackChangesData]
    )
    /**
     * Handles optimistic update for adding a new content item
     */
    const updateOptimistically = useCallback(
        (
            itemId: string,
            newElements: TextContentElement[],
            success = false,
            isPillUpdate = false,
            replacementOptionToFocus?: Partial<TextContentElement>
        ) => {
            // Get the current state from the store at execution time
            const currentNodes = useWorkspace2Store.getState()
                .contentItemViewNodes

            const updatedNodes = success
                ? updateElements(
                      itemId,
                      newElements,
                      currentNodes as ContentItemViewNodesType[],
                      isPillUpdate
                  )
                : previousNodes || []

            setContentItemViewNodes(updatedNodes)
            // If this is a ReplacementOption being depilled (OptedIn -> editable), mark it for auto-focus
            // Set immediately without requestAnimationFrame to reduce latency
            if (replacementOptionToFocus) {
                setTextContentElementToAdd(replacementOptionToFocus)
            }
        },
        [
            previousNodes,
            setContentItemViewNodes,
            setTextContentElementToAdd,
        ]
    )

    /**
     * Execute the mutation to update pill state with optimistic update
     */
    const activateDeactivateOptionItem = useCallback(
        (
            unsavedData: Partial<TextContentElement>,
            itemId: string,
            isReset?: boolean,
            replacementOptionToFocus?: Partial<TextContentElement>
        ) => {
            // Validate elements exist before proceeding
            if (!unsavedData.elements || unsavedData.elements.length === 0) {
                console.error(
                    'No elements to update in activateDeactivateOptionItem'
                )
                return
            }

            updateOptimistically(
                itemId,
                unsavedData.elements as TextContentElement[],
                true,
                true
            )

            const elementsData = (isReset
                ? unsavedData.elements
                : [unsavedData]) as TextContentElement[]
            const elementId = isReset ? elementsData[0].id : itemId

            executeWithOptimistic({
                fallbackState: nodes,
                asyncAction: async () => {
                    return await mutateContentElements(
                        elementId,
                        elementsData,
                        !!isReset
                    )
                },
                successCbFn: async () => {
                    if (replacementOptionToFocus) {
                        requestAnimationFrame(() => {
                            setTextContentElementToAdd(replacementOptionToFocus)
                        })
                    }
                    await debouncedFetchTrackChanges()
                },
                errorCbFn: () => {
                    if (!unsavedData.elements) return

                    updateOptimistically(
                        itemId,
                        unsavedData.elements as TextContentElement[]
                    )
                },
            })
        },
        [
            executeWithOptimistic,
            updateOptimistically,
            mutateContentElements,
            debouncedFetchTrackChanges,
            setTextContentElementToAdd,
            nodes,
        ]
    )

    /**
     * Register a pill selection with the store
     */
    const register = useCallback(
        (
            selectedPill: Partial<TextContentElement>,
            parentItem: Partial<TextContentElement>,
            isSelected: boolean = false,
            contentItemViewNodes: Partial<ContentItemViewNodesType>[] = [],
            multiType: string = ''
        ) => {
            setPillSelection({
                selectedPill,
                parentItem,
                isSelected,
                contentItemViewNodes,
                multiType,
            })
        },
        [setPillSelection]
    )

    /**
     * Handles click events on pill items (selection options, option groups, etc.)
     * Changes the state of the pill based on its current selection state and type
     */
    const handlePillClick = useCallback(
        (
            selectedItem: Partial<TextContentElement>,
            isSelected: boolean,
            parentItem: Partial<TextContentElement>,
            multiType?: string
        ) => {
            if (!selectedItem?.id || !selectedItem.type || !parentItem?.id)
                return

            const isValid = isValidVariant(selectedItem.type)

            const newValue = calculatePillStateChange(
                multiType ?? '',
                isSelected,
                isValid
            )

            const updated = updateElementById(
                parentItem as TextContentElement,
                selectedItem as TextContentElement,
                selectedItem.id,
                newValue
            )
            if (
                selectedItem.type === 'ReplacementOption' &&
                newValue !== PROPERTY_EDITOR_PILL_STATE_VARIANT.OptedIn &&
                !isSelected
            ) {
                // ReplacementOption being depilled (clicked to edit)
                updateOptimistically(
                    parentItem.id,
                    updated.elements as TextContentElement[],
                    true,
                    true,
                    selectedItem
                )
            } else if (
                selectedItem.type === 'ReplacementOption' &&
                newValue === PROPERTY_EDITOR_PILL_STATE_VARIANT.InActive &&
                isSelected
            ) {
                // ReplacementOption OptedIn → editable (clicked to edit)
                // DON'T update the store - just trigger auto-focus via textContentElementToAdd
                // The ReplacementOption component will handle showing editable UI via local 'active' state
                // This avoids the slow store update and component re-render

                // Pass the element with explicit OptedIn state so the component knows the original state
                const elementWithOriginalState = {
                    ...selectedItem,
                    optionItemState: PROPERTY_EDITOR_PILL_STATE_VARIANT.OptedIn,
                }

                // Just trigger the auto-focus - no store update needed
                setTextContentElementToAdd(elementWithOriginalState)
            } else {
                // All other cases (SelectionOption, OptionGroup, etc.)
                activateDeactivateOptionItem(updated, parentItem.id)
            }
        },
        [
            isValidVariant,
            calculatePillStateChange,
            activateDeactivateOptionItem,
            updateOptimistically,
            setTextContentElementToAdd,
        ]
    )

    return {
        activateDeactivateOptionItem,
        handlePillClick,
        register,
        resetPillSelection: resetPillSelectionState,
    }
}
